import React, { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { Button, Card, Checkbox, DatePicker, message, Popconfirm, Select, Space, Spin } from "antd";
import { serverFetch, RECORD_PASS_VID } from "@extensions";
import "./history.css";
import { useCompany } from "@redux";

const { RangePicker } = DatePicker;

const errMsg = (e) => e?.userMessage || e?.UserMessage || "Ошибка запроса";

const emptyExtendedData = {
  accounts: [],
  deposits: [],
  certificates: [],
  abons: [],
  records: [],
};

const saleStatuses = {
  0: "Без статуса",
  1: "Новый",
  2: "Активный",
  3: "Заморожен",
  4: "Разморожен",
  5: "Закрыт",
};

const History = () => {
  const company = useCompany();
  const [extendedLoading, setExtendedLoading] = useState(false);
  const [extendedData, setExtendedData] = useState(emptyExtendedData);
  const [certificateLoadingId, setCertificateLoadingId] = useState(null);
  const [showAllCertificates, setShowAllCertificates] = useState(false);
  const [showUpcomingRecords, setShowUpcomingRecords] = useState(true);
  const [freezeRanges, setFreezeRanges] = useState({});
  const [freezingId, setFreezingId] = useState(null);
  // Активный фильтр "Показать": все / свой / конкретный участник группы
  const [activeContext, setActiveContext] = useState("all");
  const backgroundStyle = company?.catalogBackgroundImage
    ? { backgroundImage: `url("${company.catalogBackgroundImage}")` }
    : undefined;

  const fetchExtendedData = useCallback(() => {
    setExtendedLoading(true);
    serverFetch("OnlineClient/ExtendedData", {
      method: "GET",
      queryParams: {
        includeClosedCertificates: showAllCertificates,
      },
    })
      .then((data) => {
        setExtendedData({
          accounts: Array.isArray(data?.accounts) ? data.accounts : [],
          deposits: Array.isArray(data?.deposits) ? data.deposits : [],
          certificates: Array.isArray(data?.certificates) ? data.certificates : [],
          abons: Array.isArray(data?.abons) ? data.abons : [],
          records: Array.isArray(data?.records) ? data.records : [],
        });
      })
      .catch((e) => {
        message.error(errMsg(e));
        setExtendedData(emptyExtendedData);
      })
      .finally(() => setExtendedLoading(false));
  }, [showAllCertificates]);

  useEffect(() => {
    fetchExtendedData();
  }, [fetchExtendedData]);

  // Все позиции истории: по ним собираем участников группы и количества в пилюлях.
  const allHistoryItems = [
    ...(extendedData.accounts || []),
    ...(extendedData.deposits || []),
    ...(extendedData.certificates || []),
    ...(extendedData.abons || []),
    ...(extendedData.records || []),
  ];

  // У позиций представителя clientId не задан
  const familyIds = new Set();
  const familyNames = {};
  allHistoryItems.forEach((item) => {
    if (item?.clientId && !familyIds.has(item.clientId)) {
      familyIds.add(item.clientId);
      familyNames[String(item.clientId)] = item.clientName || "Без имени";
    }
  });

  const contextOptions = [{ key: "all", label: "Все" }, { key: "self", label: "Моя" }];
  familyIds.forEach((id) => {
    contextOptions.push({ key: id, label: familyNames[String(id)] });
  });

  // Отбор позиций по выбранному контексту
  const filterByContext = (items) => {
    if (activeContext === "all") return items;
    if (activeContext === "self") return items.filter((item) => !item?.clientId);
    return items.filter((item) => item?.clientId === activeContext);
  };

  const contextTotalCount = allHistoryItems.length;
  const contextCounts = {};
  allHistoryItems.forEach((item) => {
    const key = item?.clientId ? String(item.clientId) : "self";
    contextCounts[key] = (contextCounts[key] || 0) + 1;
  });


  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return dayjs(dateStr).format("DD.MM.YYYY");
  };

  const formatPrice = (price) => {
    const value = Number(price || 0);
    return value.toLocaleString("ru-RU");
  };

  const formatQty = (qty) => {
    const value = Number(qty || 0);
    return value.toLocaleString("ru-RU");
  };

  const formatStatus = (status) => saleStatuses[status] || "Неизвестно";

  const formatMoney = (price) => `${formatPrice(price)} ₽`;

  const getStatusClass = (status) => {
    if (status === 2) return "history-status-active";
    if (status === 3) return "history-status-frozen";
    if (status === 5) return "history-status-closed";
    return "";
  };

  const handleCertificateView = (certificate) => {
    const certificateWindow = window.open("", "_blank");
    if (!certificateWindow) {
      message.error("Не удалось открыть окно печатной формы");
      return;
    }

    certificateWindow.document.write("<!doctype html><html><head><title>Сертификат</title></head><body>Загрузка...</body></html>");
    certificateWindow.document.close();

    setCertificateLoadingId(certificate.id);
    serverFetch(`OnlineClient/CertificatePrint/${certificate.id}`, { method: "GET" })
      .then((data) => {
        certificateWindow.document.open();
        certificateWindow.document.write(data?.html || "");
        certificateWindow.document.close();
        window.setTimeout(() => {
          certificateWindow.focus();
          certificateWindow.print();
        }, 300);
      })
      .catch((e) => {
        certificateWindow.close();
        message.error(errMsg(e));
      })
      .finally(() => setCertificateLoadingId(null));
  };

  const handleFreezeRangeChange = (saleId, value) => {
    setFreezeRanges((prev) => ({
      ...prev,
      [saleId]: value,
    }));
  };

  const handleFreeze = (sale) => {
    const range = freezeRanges[sale.id];
    if (!Array.isArray(range) || range.length !== 2 || !range[0] || !range[1]) {
      message.warning("Укажите период заморозки");
      return;
    }

    setFreezingId(sale.id);
    serverFetch("OnlineClient/FreezeAbon", {
      method: "POST",
      bodyData: {
        saleId: sale.id,
        date: range[0].format("YYYY-MM-DD"),
        dateEnd: range[1].format("YYYY-MM-DD"),
        status: 3, // 3 - заморожен
      },
    })
      .then(() => {
        message.success("Абонемент заморожен");
        setFreezeRanges((prev) => ({
          ...prev,
          [sale.id]: null,
        }));
        fetchExtendedData();
      })
      .catch((e) => message.error(errMsg(e)))
      .finally(() => setFreezingId(null));
  };

  const renderBalanceCard = (item, type) => (
    <div key={`${type}-${item.id || item.name}-${item.clientId || "self"}`} className="history-wallet-balance">
      <div className="history-wallet-balance-icon">{type === "deposit" ? "%" : "₽"}</div>
      <div className="history-wallet-balance-sum">{formatMoney(item.syma)}</div>
      <div className="history-wallet-balance-name">{item.name || (type === "deposit" ? "Депозит" : "Счет")}</div>
      {/* Для участников группы показываем, чей это счёт */}
      {item.clientId && (
        <div className="history-wallet-balance-owner">{item.clientName || "Участник группы"}</div>
      )}
    </div>
  );

  const renderWallet = () => {
    const accounts = filterByContext(extendedData.accounts || []);
    const deposits = filterByContext(extendedData.deposits || []);
    const rawBalances = [
      ...accounts.map((account) => ({ ...account, type: "account" })),
      ...deposits.map((deposit) => ({ ...deposit, type: "deposit" })),
    ];
    const seenBalances = new Set();
    const balances = rawBalances.filter((balance) => {
      const name = (balance.name || (balance.type === "deposit" ? "Депозит" : "Счет")).trim().toLowerCase();
      const key = `${balance.clientId || "self"}-${name}-${Number(balance.syma || 0)}`;

      if (seenBalances.has(key)) return false;
      seenBalances.add(key);
      return true;
    });

    if (balances.length === 0) return null;

    return (
      <div className="history-wallet">
        <div className="history-section-head">
          <div className="history-section-title">Счета</div>
        </div>
        <div className="history-wallet-list">
          {balances.map((item) => renderBalanceCard(item, item.type))}
        </div>
      </div>
    );
  };

  const renderSection = (title, extra, children, className = "") => (
    <Card className={`history-panel ${className}`} bordered={false}>
      <div className="history-section-head">
        <div className="history-section-title">{title}</div>
        {extra}
      </div>
      <div className="history-list">{children}</div>
    </Card>
  );

  const renderCertificates = () => {
    const certificates = filterByContext(extendedData.certificates || []);
    if (certificates.length === 0 && showAllCertificates) return null;

    const filter = (
      <div className="history-section-action">
        <Checkbox
          checked={showAllCertificates}
          disabled={extendedLoading}
          className="history-certificate-filter"
          onChange={(e) => setShowAllCertificates(e.target.checked)}
        >
          Показать погашенные
        </Checkbox>
      </div>
    );

    return renderSection(
      "Сертификаты",
      filter,
      certificates.length > 0 ? (
        certificates.map((certificate) => (
          <div key={certificate.id} className="history-item">
            <div className="history-item-icon">→</div>
            <div className="history-item-body">
              <div className="history-item-name">{certificate.productName || `Сертификат №${certificate.id}`}</div>
              <div className="history-item-meta">
                <span>{formatDate(certificate.docDate)}</span>
                <span>№{certificate.id}</span>
                <span className={getStatusClass(certificate.status)}>{formatStatus(certificate.status)}</span>
                {certificate.finish && <span>до {formatDate(certificate.finish)}</span>}
                {certificate.clientId && (
                  <span className="history-item-owner"><span>{certificate.clientName || "Участник группы"}</span></span>
                )}
              </div>
            </div>
            <div className="history-item-side">
              <div className="history-item-price">{formatMoney(certificate.syma)}</div>
              <Button
                size="small"
                className="history-item-button"
                loading={certificateLoadingId === certificate.id}
                onClick={() => handleCertificateView(certificate)}
              >
                Просмотр
              </Button>
            </div>
          </div>
        ))
      ) : (
        <div className="history-empty">Нет сертификатов</div>
      ),
      "history-certificates-panel",
    );
  };

  const renderAbons = () => {
    const abons = filterByContext(extendedData.abons || []);
    if (abons.length === 0) return null;

    return renderSection(
      "Абонементы",
      null,
      abons.map((abon) => {
        const canFreeze = abon.status === 2 && abon.canBeFrozen;
        const packageItems = Array.isArray(abon.packageItems) ? abon.packageItems : [];

        return (
          <div key={abon.id} className="history-item history-item-with-actions">
            <div className="history-item-icon">→</div>
            <div className="history-item-body">
              <div className="history-item-name">{abon.productName || `Продажа №${abon.id}`}</div>
              <div className="history-item-meta">
                <span>{formatDate(abon.docDate)}</span>
                <span className={getStatusClass(abon.status)}>{formatStatus(abon.status)}</span>
                {abon.finish && <span>до {formatDate(abon.finish)}</span>}
                {abon.clientId && (
                  <span className="history-item-owner"><span>{abon.clientName || "Участник группы"}</span></span>
                )}
              </div>
              {packageItems.length > 0 && (
                <div className="history-package-rests">
                  {packageItems.map((item, index) => (
                    <div key={`${abon.id}-${item.productName}-${index}`} className="history-package-rest">
                      <span>{item.productName || "Пакет"}</span>
                      <span>
                        Остаток: {formatQty(item.qtyRest)} из {formatQty(item.qty)}
                        {item.expUnit ? ` ${item.expUnit}` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="history-item-side">
              <div className="history-item-price">{formatMoney(abon.syma)}</div>
              {canFreeze && (
                <Space className="history-freeze-actions" wrap>
                  <RangePicker
                    value={freezeRanges[abon.id] || null}
                    format="DD.MM.YYYY"
                    onChange={(value) => handleFreezeRangeChange(abon.id, value)}
                  />
                  <Popconfirm
                    title="Заморозить?"
                    description="Заморозить абонемент на выбранный период?"
                    okText="Да"
                    cancelText="Нет"
                    onConfirm={() => handleFreeze(abon)}
                  >
                    <Button size="small" className="history-item-button" loading={freezingId === abon.id}>
                      Заморозить
                    </Button>
                  </Popconfirm>
                </Space>
              )}
            </div>
          </div>
        );
      }),
      "history-abons-panel",
    );
  };

  const formatRecordPassVid = (passVid) => {
    if (passVid === RECORD_PASS_VID.ByAbon) return "За абонемент";
    if (passVid === RECORD_PASS_VID.ByPacket) return "За услуги пакета";
    if (passVid === RECORD_PASS_VID.ByPayment) return "За плату";
    return "";
  };

  const formatScheduleRecord = (record) => {
    if (!record?.docDate) return "";
    const date = formatDate(record.docDate);
    const start = record.start ? dayjs(record.start, "HH:mm:ss").format("HH:mm") : "";
    const finish = record.finish ? dayjs(record.finish, "HH:mm:ss").format("HH:mm") : "";
    if (start && finish) {
      return `${date} ${start}-${finish}`;
    }
    return date;
  };

  const renderRecords = () => {
    const records = filterByContext(extendedData.records || []);
    if (records.length === 0) return null;

    const today = dayjs().startOf("day");
    const filteredRecords = showUpcomingRecords
      ? records.filter((record) => record.docDate && (dayjs(record.docDate).isSame(today, "day") || dayjs(record.docDate).isAfter(today)))
      : records;

    const sortedRecords = filteredRecords.slice().sort((a, b) => {
      const aTime = a.docDate ? dayjs(a.docDate).valueOf() : 0;
      const bTime = b.docDate ? dayjs(b.docDate).valueOf() : 0;
      if (bTime !== aTime) return bTime - aTime;
      const aStart = a.start ? dayjs(a.start, "HH:mm:ss").valueOf() : 0;
      const bStart = b.start ? dayjs(b.start, "HH:mm:ss").valueOf() : 0;
      return bStart - aStart;
    });

    const recordFilter = (
      <div className="history-section-action">
        <Checkbox
          checked={showUpcomingRecords}
          disabled={extendedLoading}
          className="history-record-filter"
          onChange={(e) => setShowUpcomingRecords(e.target.checked)}
        >
          Предстоящие записи
        </Checkbox>
      </div>
    );

    return renderSection(
      "Записи",
      recordFilter,
      filteredRecords.length > 0 ? (
        sortedRecords.map((record) => (
          <div key={record.id} className="history-item history-record-row">
            <div className="history-item-icon">→</div>
            <div className="history-record-main">
              <div className="history-item-name">{record.productName || `Запись №${record.id}`}</div>
              <div className="history-item-meta">
                <span>{formatScheduleRecord(record)}</span>
                {formatRecordPassVid(record.passVid) && (
                  <span>{formatRecordPassVid(record.passVid)}</span>
                )}
                {record.clientId && (
                  <span className="history-item-owner"><span>{record.clientName || "Участник группы"}</span></span>
                )}
              </div>
            </div>
            <div className="history-record-side">
              {(record.userName || record.roomName) && (
                <>
                  {record.userName && <div>{record.userName}</div>}
                  {record.roomName && <div>{record.roomName}</div>}
                </>
              )}
            </div>
          </div>
        ))
      ) : (
        <div className="history-empty">Нет записей</div>
      ),
      "history-records-panel",
    );
  };

  const renderExtendedData = () => {
    const hasExtendedData =
      (extendedData.accounts || []).length > 0 ||
      (extendedData.certificates || []).length > 0 ||
      (extendedData.abons || []).length > 0 ||
      (extendedData.deposits || []).length > 0 ||
      (extendedData.records || []).length > 0 ||
      !showAllCertificates;

    if (extendedLoading) {
      return (
        <div className="history-loading">
          <Spin />
        </div>
      );
    }

    if (!hasExtendedData) return null;

    return (
      <div className="history-extended">
        {renderWallet()}
        {renderCertificates()}
        {renderAbons()}
        {renderRecords()}
      </div>
    );
  };

  return (
    <div className="history-context" style={backgroundStyle}>
      {contextOptions.length > 2 && (
        <div className="history-family-context">
          <span className="history-context-label">История:</span>
          <Select
            className="history-context-select"
            value={activeContext}
            onChange={(value) => setActiveContext(value)}
            options={contextOptions.map((opt) => ({
              value: opt.key,
              label: `${opt.label} (${opt.key === "all" ? contextTotalCount : contextCounts[String(opt.key)] || 0})`,
            }))}
          />
        </div>
      )}
      {renderExtendedData()}
    </div>
  );
};

export default History;
